import React, { useRef, useEffect } from "react";

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MIN_HEIGHT = 46;
const MAX_HEIGHT = 180;

export function ChatInput({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder,
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) {
        onSubmit(value);
        onChange("");
      }
    }
  };

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height =
      Math.min(Math.max(ta.scrollHeight, MIN_HEIGHT), MAX_HEIGHT) + "px";
  }, [value]);

  const canSend = value.trim() && !disabled;

  return (
    <div className="relative flex items-end bg-white rounded-2xl border border-slate-200 shadow-sm shadow-slate-900/[0.04] focus-within:border-[#005bbb]/50 focus-within:ring-4 focus-within:ring-[#005bbb]/[0.08] transition-all">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        // Firefox doesn't always move keyboard focus into an iframe nested in
        // the embed.js widget's shadow DOM on click; focus explicitly.
        onMouseDown={() => textareaRef.current?.focus()}
        disabled={disabled}
        placeholder={placeholder}
        rows={1}
        className="flex-1 min-w-0 px-4 py-3 pr-13 bg-transparent focus:outline-none text-[15px] text-slate-800 placeholder:text-slate-400 rounded-2xl resize-none leading-relaxed"
        style={{ minHeight: MIN_HEIGHT, maxHeight: MAX_HEIGHT }}
      />
      <button
        onClick={() => {
          if (canSend) {
            onSubmit(value);
            onChange("");
          }
        }}
        disabled={!canSend}
        aria-label="Send message"
        className={`absolute right-2 bottom-2 w-8 h-8 flex items-center justify-center rounded-xl transition-all ${
          canSend
            ? "bg-[#005bbb] text-white hover:bg-[#004a96] active:scale-90 shadow-sm shadow-blue-900/20"
            : "bg-slate-100 text-slate-300 cursor-not-allowed"
        }`}
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2.2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4.5 19.5l15-7.5-15-7.5v6l10.5 1.5-10.5 1.5v6z"
          />
        </svg>
      </button>
    </div>
  );
}
